/* istanbul ignore file */

import { useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { useHttpClient } from '@bonusly/http-client';
import { Permission, usePermission } from '@bonusly/who-am-i';

import { ResourcePath } from './ResourcePath';
import { CompanyItem, FeaturedItemResponse, QueryKey } from './types';

export const useRetrieveFeaturedItems = (options: UseQueryOptions<AxiosResponse<FeaturedItemResponse>, unknown, CompanyItem[]> = {}) => {
	const canClaimPointsBoost = usePermission(Permission.CanClaimPointsBoost);
	const http = useHttpClient('rcs');

	return useQuery<AxiosResponse<FeaturedItemResponse>, unknown, CompanyItem[]>(
		[QueryKey.CatalogsFeatured],
		() => http.get<FeaturedItemResponse>(ResourcePath.CatalogsFeatured),
		{
			refetchOnWindowFocus: false,
			retry: false,
			select: ({ data }) => {
				const { previously_claimed, points_boost, promoted } = data.data;
				// NOTE: if user cannot give, do not include points boost
				const featuredItems = canClaimPointsBoost ? [...points_boost, ...promoted] : [...promoted];

				// NOTE: add forYou tag for previously claimed items
				previously_claimed.forEach((claimedItem) => {
					featuredItems.push({
						...claimedItem,
						item: {
							...claimedItem.item,
							forYou: true,
						},
					});
				});

				return featuredItems;
			},
			...options,
		}
	);
};

export const useInvalidateFeaturedItems = () => {
	const queryClient = useQueryClient();

	return () => {
		queryClient.invalidateQueries([QueryKey.CatalogsFeatured]);
	};
};
